import { calculateTotal } from '@containers/leads/quotationTool/constants';

export const QUOTATION_GST_RATE = 0.18;
const MM2_PER_SQFT = 92903.04;

const toFiniteNumber = (value: unknown) => {
	const numericValue = Number(value);
	return Number.isFinite(numericValue) ? numericValue : 0;
};

/** Floor area in sqft for a SQFT quote line (mm measurements). Null when not SQFT / not computable. */
export function quotationLineSqft(product?: Record<string, any> | null): number | null {
	if (!product) return null;
	const uom = String(product.unitOfMeasure ?? product.unit_of_measure ?? '').toUpperCase();
	if (uom !== 'SQFT') return null;
	const calc = String(product.calculation ?? '');
	const measurement = product.measurement || {};
	const width = toFiniteNumber(measurement.width);
	const height = toFiniteNumber(measurement.height);
	const depth = toFiniteNumber(measurement.depth);
	const area = toFiniteNumber(measurement.area);
	if (/W/i.test(calc) && /D/i.test(calc) && width > 0 && depth > 0) return (width * depth) / MM2_PER_SQFT;
	if (/W/i.test(calc) && /H/i.test(calc) && width > 0 && height > 0) return (width * height) / MM2_PER_SQFT;
	if (/H/i.test(calc) && /D/i.test(calc) && height > 0 && depth > 0) return (height * depth) / MM2_PER_SQFT;
	if (/Area/i.test(calc) && area > 0) return area > 1000 ? area / MM2_PER_SQFT : area;
	return null;
}

/** Whole-rupee ceiling used for quotation line prices, builder totals, and aggregates. */
export const ceilQuotationAmount = (value: unknown) => Math.ceil(toFiniteNumber(value));

/**
 * Turn a BOM / builder total into the line's unitCost.
 * SQFT lines keep a per-sqft rate so calculateTotal (W×D×R / 92903) does not re-multiply the total.
 */
export function unitCostForQuotedTotal(product: Record<string, any> | null | undefined, totalPrice: number): number {
	const total = toFiniteNumber(totalPrice);
	const sqft = quotationLineSqft(product);
	if (sqft != null && sqft > 0) return total / sqft;
	const quantity = Math.max(1, toFiniteNumber(product?.quantity) || 1);
	return total / quantity;
}

/** Stamp price + unitCost after a builder/BOM calculate without breaking SQFT quote math. */
export function applyQuotedTotalToLine<T extends Record<string, any>>(product: T, totalPrice: number): T {
	const total = ceilQuotationAmount(totalPrice);
	return {
		...product,
		price: total,
		total,
		unitCost: unitCostForQuotedTotal(product, total)
	};
}

export const isQuotationProductActive = (product?: Record<string, any> | null) => product?.active !== false;

export const getQuotationProductGrossTotal = (
	product?: Record<string, any> | null,
	options: { includeInactive?: boolean; preferStoredPrice?: boolean } = {}
) => {
	if (!product) return 0;
	if (!options.includeInactive && !isQuotationProductActive(product)) return 0;
	// Saved quotation prices already include the server's allocation and line-rounding policy.
	if (options.preferStoredPrice && product.price != null && Number.isFinite(Number(product.price))) {
		return Math.round(Number(product.price));
	}
	const priceAdjustment = product.grossUpPriceAdjustment ?? product.gross_up_price_adjustment;
	if (priceAdjustment?.applied === true && product.price != null) return Math.round(toFiniteNumber(product.price));

	const shouldUseStoredPrice =
		product.builder || (options.preferStoredPrice && product.price !== null && product.price !== undefined);
	const rawTotal = shouldUseStoredPrice ? product.price || 0 : calculateTotal(product);
	// Ceiling each line so area subtotals and grand total always sum from the same whole-rupee amounts.
	return ceilQuotationAmount(rawTotal);
};

export const getQuotationProductsGrossTotal = (
	products: Array<Record<string, any>> = [],
	options: { includeInactive?: boolean; extras?: boolean | null; preferStoredPrice?: boolean } = {}
) =>
	products.reduce((sum, product) => {
		if (options.extras !== undefined && options.extras !== null && Boolean(product?.extras) !== options.extras) {
			return sum;
		}
		return sum + getQuotationProductGrossTotal(product, options);
	}, 0);

export const getQuotationTaxBreakup = (grossTotal: number, gstRate = QUOTATION_GST_RATE) => {
	const gross = ceilQuotationAmount(grossTotal);
	const netCost = Math.round(gross / (1 + gstRate));
	const gstAmount = gross - netCost;

	return {
		netCost,
		netTotal: netCost,
		gstAmount,
		grossTotal: gross
	};
};

export const buildQuotationGstLedger = (
	grossTotal: number,
	ledgerData?: Record<string, any> | null,
	gstRate = QUOTATION_GST_RATE
) => {
	const gross = ceilQuotationAmount(grossTotal);
	if (!gross) return ledgerData;

	const { netCost, gstAmount } = getQuotationTaxBreakup(gross, gstRate);
	const cgstAmount = Math.round(gstAmount / 2);
	const sgstAmount = gstAmount - cgstAmount;
	const discountAmount = toFiniteNumber(ledgerData?.discountAmt ?? ledgerData?.discount_amt);
	const payable = Math.max(0, gross - discountAmount);

	return {
		...(ledgerData || {}),
		quotationExGst: netCost,
		quotation_ex_gst: netCost,
		cgstAmount,
		cgst_amount: cgstAmount,
		sgstAmount,
		sgst_amount: sgstAmount,
		gstAmount,
		gst_amount: gstAmount,
		quotationInclGst: gross,
		quotation_incl_gst: gross,
		quotationTotal: gross,
		quotation_total: gross,
		inclusiveAfterCash: gross,
		inclusive_after_cash: gross,
		payable,
		total: payable
	};
};
